import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTraitFilters } from '../api/filters'
import { useMarketplace } from '../context'
import { faChevronRight, faSliders, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons'
import { useCallback } from 'react'

interface ClearButtonProps {
    enabled: boolean
    onClick: () => void
}

function ClearButton({ enabled, onClick }: ClearButtonProps) {
    const handleClick = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            onClick()
        },
        [onClick]
    )
    if (!enabled) return null
    return (
        <button className='hover:text-yellow-500'>
            <FontAwesomeIcon icon={faTrash} onClick={handleClick} />
        </button>
    )
}

function Badge({ children }) {
    if (!children) return null
    return (
        <span className='inline-flex items-center justify-center rounded-full bg-violet-500 h-[1.5em] w-[1.5em] text-center'>
            <span className='text-[0.75em]'>{children}</span>
        </span>
    )
}

function Filters() {
    const { data } = useTraitFilters()
    const filters = data?.filters
    const { state, toggleFilter, clearFilters, closeUI } = useMarketplace()
    const entries = filters ? Object.entries(filters) : []
    const hasActive = Object.keys(state.filters).length > 0
    const selectedCount = Object.values(state.filters).reduce((acc, values) => acc + values.length, 0) || null

    return (
        <div className='container-inline min-w-[220px] max-w-[320px] w-full h-full uppercase font-bold whitespace-nowrap underline-offset-4'>
            <div className='text-[4cqi] flex flex-col h-full bg-black border-r md:border-none border-white/20'>
                <div className='flex flex-row items-center justify-between gap-x-[1em] px-[1em] border-b md:border-none border-white/20 p-4'>
                    <div className='flex-1 font-bold uppercase inline-flex gap-x-[1em] items-center'>
                        <FontAwesomeIcon icon={faSliders} />
                        <span>Filters</span>
                        <Badge>{selectedCount}</Badge>
                    </div>
                    <ClearButton enabled={hasActive} onClick={clearFilters} />
                    <FontAwesomeIcon
                        icon={faXmark}
                        onClick={() => closeUI('filtersOpen')}
                        className='md:hidden text-[1.5em] cursor-pointer'
                    />
                </div>
                <div className='flex-1 overflow-y-auto p-4 flex flex-col gap-y-[1em]'>
                    {entries.map(([traitType, traitValues]) => {
                        const activeValues = state.filters[traitType] ?? []
                        const isActive = activeValues.length > 0
                        const selectedCount = activeValues.length || null
                        return (
                            <details key={traitType} className='border-gradient group'>
                                <summary className='p-[1em] hover:bg-white/10 cursor-pointer flex flex-row items-center justify-between gap-x-[1em]'>
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className='text-[0.7em] transition-transform group-open:rotate-90'
                                    />
                                    <span className='flex-1 overflow-hidden overflow-ellipsis'>{traitType}</span>
                                    <Badge>{selectedCount}</Badge>
                                    <ClearButton enabled={isActive} onClick={() => clearFilters(traitType)} />
                                </summary>
                                <div className='flex flex-col gap-[1em] p-[1em] border-t border-white/20'>
                                    {traitValues.map((value) => (
                                        <label key={value} className='flex items-center gap-[0.5em] cursor-pointer'>
                                            <input
                                                className=' accent-violet-500'
                                                type='checkbox'
                                                name={`${traitType}:${value}`}
                                                checked={state.filters[traitType]?.includes(value) ?? false}
                                                onChange={() => toggleFilter(traitType, value)}
                                            />
                                            <span
                                                title={value}
                                                className='overflow-hidden overflow-ellipsis hover:underline'
                                            >
                                                {value}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </details>
                        )
                    })}
                </div>
                <div className='grid grid-cols-2 p-4 gap-4 border-t border-white/20 font-bold md:hidden'>
                    <button
                        onClick={() => {
                            clearFilters()
                            closeUI('filtersOpen')
                        }}
                        className='bg-white/10 p-2 rounded'
                    >
                        CLEAR
                    </button>
                    <button onClick={() => closeUI('filtersOpen')} className='bg-br1-gradient p-2 rounded'>
                        SHOW
                    </button>
                </div>
            </div>
        </div>
    )
}

export { Filters }
